import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { PositionClosing } from "../components/PositionClosing";
import { api, type ClosingStatus, type Account, type AccountPeriodDetail, type MovementLine, type MovementsSummary, type MovementsView, type SummaryColumn } from "../api";
import { HistoryModal } from "../components/HistoryModal";
import { TransferActions, trTitle } from "../components/TransferActions";
import { ClipMark, LateDays, SeriesMark, SituationLegend, SituationMark } from "../components/Situation";
import { TxActions } from "../components/TxActions";
import { Btn, Field, Input, Notice, ScrollSentinel, Select } from "../components/ui";
import { fmtDay, formatMoney } from "../lib/money";
import { useTxFlow } from "../txflow";
import { usePeriod } from "../period";
import { ManualDialog, UndoDialog, type Move } from "./conciliation/Moves";

const LIMIT = 200;
const MAX_LINES = 1000;   // teto do servidor por consulta
const ALL = "all";
type Msg = { kind: "ok" | "error"; text: string };
type Cur = "BRL" | "USD";
type Dlg = { k: "match" | "undo" | "history"; m: Move } | null;

const SITUATIONS: [string, string, string | undefined][] = [
  ["ativos", "Ativos (realizados e planejados)", undefined],
  ["realizado", "Realizados", "realizado"],
  ["vencido", "Vencidos (pendentes)", "vencido"],
  ["agendado", "Agendados (planejados)", "agendado"],
  ["cancelado", "Cancelados", "cancelado"],
  ["todos", "Todos (inclui cancelados)", "realizado,agendado,vencido,cancelado"],
];
const QUICK: [string, string][] = [["ativos", "Todos"], ["realizado", "Realizados"], ["vencido", "Vencidos"], ["agendado", "Agendados"]];
const TYPES: [string, string][] = [["receita", "Receitas"], ["despesa", "Despesas"], ["transferencia", "Transferências"]];

/**
 * Contas e Extratos — visão integrada (DEC-112). Receitas, despesas e transferências, realizadas, vencidas, agendadas e canceladas, numa só lista.
 * Esta tela NÃO calcula saldo: ordem, saldo efetivado, saldo projetado, resumo e situação de conciliação vêm do servidor (GET /movements, motor único).
 */
export function Movements({ realm, entityId, accounts, canWrite, initialTypes }: { realm: string; entityId: string; accounts: Account[]; canWrite: boolean; initialTypes?: string[] }) {
  const flow = useTxFlow();
  const [accountId, setAccountId] = useState("");   // "" = ainda não definida; ALL = todas as contas
  const per = usePeriod();
  const [from, setFrom] = useState(per.from);
  const [to, setTo] = useState(per.to);
  // O seletor de mês do cabeçalho redefine o intervalo (os campos De/Até continuam editáveis).
  useEffect(() => { setFrom(per.from); setTo(per.to); }, [per.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const [cutoff, setCutoff] = useState("");
  const [types, setTypes] = useState<string[]>(initialTypes ?? []);
  const [sit, setSit] = useState("ativos");
  const [recon, setRecon] = useState("");
  const [q, setQ] = useState("");
  const [adv, setAdv] = useState(false);
  const [pages, setPages] = useState(1);   // rolagem contínua: cada vez que se chega perto do fim, a janela cresce em LIMIT linhas
  const [view, setView] = useState<MovementsView | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [msg, setMsg] = useState<Msg | null>(null);
  const [dlg, setDlg] = useState<Dlg>(null);
  const seq = useRef(0);   // só vale a resposta da requisição mais recente

  useEffect(() => { setAccountId((cur) => (cur === ALL || (cur !== "" && accounts.some((a) => a.id === cur)) ? cur : (accounts.find((a) => a.status === "ativa") ?? accounts[0])?.id ?? "")); }, [accounts]);
  const account = accounts.find((a) => a.id === accountId);

  const load = useCallback(async () => {
    if (!accountId) return;
    const my = ++seq.current;
    const qs = new URLSearchParams({ entityId, from, to, limit: String(Math.min(LIMIT * pages, MAX_LINES)), offset: "0" });
    if (accountId !== ALL) qs.set("accountId", accountId);
    if (cutoff) qs.set("cutoff", cutoff);
    if (types.length > 0 && types.length < TYPES.length) qs.set("type", types.join(","));
    const s = SITUATIONS.find((x) => x[0] === sit)?.[2];
    if (s) qs.set("situation", s);
    if (recon) qs.set("reconciled", recon);
    if (q.trim()) qs.set("q", q.trim());
    try { const r = await api<MovementsView>("GET", `/api/${realm}/movements?${qs}`); if (my === seq.current) { setView(r); setState("ok"); } } catch { if (my === seq.current) setState("error"); }
  }, [realm, entityId, accountId, from, to, cutoff, types, sit, recon, q, pages]);
  useEffect(() => { setState("loading"); void load(); }, [load, flow.version]);
  useEffect(() => { setPages(1); }, [accountId, from, to, cutoff, types, sit, recon, q]);

  const single = view?.mode === "conta";
  // Detalhamento do período e limite da conta (uma conta selecionada); recarrega junto com a visão
  const [detail, setDetail] = useState<AccountPeriodDetail | null>(null);
  useEffect(() => {
    if (!accountId || accountId === ALL || !from || !to) { setDetail(null); return; }
    let alive = true;
    void api<AccountPeriodDetail>("GET", `/api/${realm}/accounts/${accountId}/period-detail?from=${from}&to=${to}`).then((d) => { if (alive) setDetail(d); }).catch(() => { if (alive) setDetail(null); });
    return () => { alive = false; };
  }, [realm, accountId, from, to, view]);
  const [closing, setClosing] = useState<ClosingStatus | null>(null);
  const fullMonth = !!from && !!to && from.slice(0, 7) === to.slice(0, 7) && from.endsWith("-01") && to >= from && endOfMonthIso(from) === to;
  useEffect(() => { setClosing(null); }, [accountId, from, to]);
  const closedThrough = single ? closing?.closedThrough ?? null : null;
  const accCur: Cur | undefined = view?.balances.accounts[0]?.currency;
  const proj = view?.projection;
  const projLabel = proj ? (proj.cutoffIsEndOfMonth ? `Saldo previsto até o fim do mês (${fmtDay(proj.cutoff)})` : `Saldo previsto até ${fmtDay(proj.cutoff)}`) : "Saldo previsto";

  const onChanged = () => { void load(); };
  const activeAdv = (recon ? 1 : 0) + (types.length ? 1 : 0) + (cutoff ? 1 : 0) + (sit !== "ativos" && !QUICK.some(([k]) => k === sit) ? 1 : 0);
  const toggleType = (t: string) => setTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  function moveOf(l: MovementLine, leg: "saida" | "entrada" | null): Move {
    if (l.entryType === "lancamento") {
      return { key: `x-${l.id}`, sourceType: "transaction", sourceId: l.id, leg: null, date: l.settledDate ?? l.plannedDate, title: l.description, sub: l.counterpartyName,
        amountDecimal: l.amount.decimal, currency: l.currency, negative: l.kind === "despesa", reconciled: l.reconciliation.state === "conciliado", reconciliationId: l.reconciliation.reconciliationId, retroactive: l.retroactive };
    }
    const out = leg === "saida";
    return { key: `t-${l.id}-${leg}`, sourceType: "transfer", sourceId: l.id, leg, date: l.settledDate ?? l.plannedDate,
      title: `${trTitle(l)}${view?.mode === "consolidado" ? ` (${out ? "saída" : "entrada"})` : ""}`, sub: `${l.fromAccountName} → ${l.toAccountName}`,
      amountDecimal: (out ? l.fromAmount : l.toAmount).decimal, currency: out ? l.fromCurrency : l.toCurrency, negative: out,
      reconciled: !!(view?.mode === "consolidado" ? l.reconciliation.legs?.[leg ?? "saida"] : l.reconciliation.reconciliationId), reconciliationId: view?.mode === "consolidado" ? (l.reconciliation.legs?.[leg ?? "saida"] ?? null) : l.reconciliation.reconciliationId, retroactive: l.retroactive };
  }

  /** Itens de conciliação do menu ⋮: levam ao fluxo EXISTENTE (mesmos diálogos, mesmas APIs e mesmas travas do servidor). */
  function reconItems(l: MovementLine): ReactNode {
    if (!canWrite && l.reconciliation.state === "nao_aplicavel") return null;
    if (l.situation !== "realizado") return null;
    const legs: ("saida" | "entrada" | null)[] = l.entryType === "lancamento" ? [null] : single ? [l.leg ?? "saida"] : ["saida", "entrada"];
    return legs.map((leg) => {
      const m = moveOf(l, leg);
      const suffix = leg && !single ? ` da ${leg === "saida" ? "saída" : "entrada"}` : "";
      return (
        <span key={leg ?? "x"} style={{ display: "contents" }}>
          {canWrite && !m.reconciled && (l.entryType === "transferencia" || l.status === "confirmado") && <button className="link" onClick={() => setDlg({ k: "match", m })} data-act="reconcile">Conciliar{suffix}</button>}
          {canWrite && m.reconciled && m.reconciliationId && <button className="link" onClick={() => setDlg({ k: "undo", m })} data-act="unreconcile">Desfazer conciliação{suffix}</button>}
          {m.reconciled && m.reconciliationId && <button className="link" onClick={() => setDlg({ k: "history", m })} data-act="recon-history">Ver vínculo{suffix}</button>}
        </span>
      );
    });
  }

  function reconCell(l: MovementLine): ReactNode {
    const r = l.reconciliation;
    if (r.state === "nao_aplicavel") return <span className="muted">—</span>;
    const legsInfo = !single && r.legs ? <div className="muted small" data-recon-legs>Saída {r.legs.saida ? "✓" : "—"} · Entrada {r.legs.entrada ? "✓" : "—"}</div> : null;
    if (r.state === "conciliado") return <><span className="recon-ok" data-badge-reconciled data-recon-state="conciliado" title="Conciliado">✓ Conciliado</span>{legsInfo}</>;
    if (r.state === "parcial") return <><span className="pill" data-recon-state="parcial">Parcial</span>{legsInfo}</>;
    return <><span className="muted" data-recon-state="pendente" title="Não conciliado">—</span>{legsInfo}</>;
  }

  function row(l: MovementLine) {
    const tr = l.entryType === "transferencia";
    const cur: Cur = tr ? (single && l.leg === "entrada" ? l.toCurrency : l.fromCurrency) : l.currency;
    let value: ReactNode;
    if (!tr) value = <span className={`tabular ${l.kind === "receita" ? "pos" : "neg"}`}>{l.kind === "receita" ? "+" : "−"} {formatMoney(l.amount.decimal, l.currency)}</span>;
    else if (single && l.leg) value = <span className={`tabular ${l.leg === "entrada" ? "pos" : "neg"}`}>{l.leg === "entrada" ? "+" : "−"} {formatMoney(l.legAmount!.decimal, cur)}</span>;
    else value = <span className="tabular">{formatMoney(l.fromAmount.decimal, l.fromCurrency)}{l.crossCurrency && <> → {formatMoney(l.toAmount.decimal, l.toCurrency)}</>}</span>;
    const settledDiffers = l.situation === "realizado" && l.settledDate && l.settledDate !== l.plannedDate;
    const key = `${l.entryType}-${l.id}`;
    const title = tr ? trTitle(l) : l.description;
    return (
      <tr key={key} className={l.situation === "cancelado" ? "inactive" : ""} data-situation={l.situation} {...(tr ? { "data-transfer-line": title, "data-transfer": title } : { "data-tx": l.description })}>
        <td className="sit-col"><SituationMark sit={l.situation} extra={tr && l.status === "planejado" ? "ainda não efetivada" : undefined} /></td>
        <td className="tabular">{fmtDay(l.referenceDate)}
          {l.situation === "vencido" && <LateDays planned={l.referenceDate} today={view?.asOf ?? l.referenceDate} />}
          {settledDiffers && <div className="muted small">prevista {fmtDay(l.plannedDate)}</div>}
          {l.retroactive && <div><span className="pill accent" data-badge-retro title="Lançada depois, com data anterior a hoje">Retroativo</span></div>}</td>
        <td>
          {tr ? <>
            <strong>{single && l.leg ? (l.leg === "saida" ? "Transferência para " : "Transferência de ") + l.counterAccountName : title}</strong><SeriesMark kind={l.seriesKind} no={l.installmentNo} total={l.installmentTotal} /><ClipMark count={l.attachmentCount} ownerType="transfer" ownerId={l.id} />
            <div className="muted small" data-badge-transfer>↔ {l.fromAccountName} → {l.toAccountName}{l.description && single ? ` · ${l.description}` : ""}</div>
            {l.crossCurrency && <div className="muted small tabular" data-line-fx>{formatMoney(l.fromAmount.decimal, l.fromCurrency)} → {formatMoney(l.toAmount.decimal, l.toCurrency)} · taxa {l.rateDisplay?.replace(".", ",")} BRL/USD</div>}
          </> : <>
            <strong>{l.description}</strong><SeriesMark kind={l.seriesKind} no={l.installmentNo} total={l.installmentTotal} /><ClipMark count={l.attachmentCount} ownerType="transaction" ownerId={l.id} />
            <div className="muted small">{l.categoryPath}{l.counterpartyName ? ` · ${l.counterpartyName}` : ""}</div>
            {l.treatment !== "resultado" && <div><span className="pill accent">{l.treatment === "venda_ativo" ? "Venda de ativo" : "Distribuição de lucros"}</span></div>}
          </>}
        </td>
        {!single && <td className="small">{tr ? <span className="muted">entre contas</span> : l.accountName}</td>}
        <td className="num">{value}</td>
        {single && <td className="num tabular" data-col-effective>{l.balanceEffective ? formatMoney(l.balanceEffective.decimal, cur) : ""}</td>}
        {single && <td className="num tabular" data-col-projected>{l.balanceProjected ? formatMoney(l.balanceProjected.decimal, cur) : ""}</td>}
        <td>{reconCell(l)}</td>
        <td>{closedThrough && l.referenceDate <= closedThrough ? <span className="muted" title="Posição fechada: não aceita alterações" data-frozen>🔒</span> : tr
          ? <TransferActions tr={l} canWrite={canWrite} onChanged={onChanged} onMessage={setMsg} menu extra={reconItems(l)} />
          : <TxActions tx={l} canWrite={canWrite} onChanged={onChanged} onMessage={setMsg} menu extra={reconItems(l)} />}</td>
      </tr>
    );
  }

  const cols = single ? 9 : 7;
  function body(v: MovementsView) {
    const out: ReactNode[] = [];
    let today = false, canc = false;
    const sepToday = (k: string) => (
      <tr key={k} className="mv-today" data-today-separator><td colSpan={cols}>Hoje · {fmtDay(v.asOf)}{single && accCur ? ` — saldo efetivado: ${formatMoney(v.balances.accounts[0]!.current.decimal, accCur)}` : ""}</td></tr>
    );
    for (const l of v.lines) {
      if (l.block >= 1 && !today) { out.push(sepToday("hoje")); today = true; }
      if (l.block === 2 && !canc) { out.push(<tr key="canc" className="mv-sep"><td colSpan={cols}>Cancelados — não entram em nenhum saldo</td></tr>); canc = true; }
      out.push(row(l));
    }
    if (!today && v.lines.length > 0 && from <= v.asOf && v.asOf <= to) out.push(sepToday("hoje-fim"));
    return out;
  }

  const col = (c: SummaryColumn, cur: Cur, merge: boolean) => {
    const f = (m: { decimal: string }) => formatMoney(m.decimal, cur);
    return [
      ["Saldo anterior", f(c.opening), false], ["Receitas", f(c.receitas), false],
      ...(merge ? [["Transf. entre contas", f(c.transfersOut), false]] : [["Transf. de entrada", f(c.transfersIn), false], ["Transf. de saída", f(c.transfersOut), false]]),
      ["Despesas", f(c.despesas), false], ["Resultado", f(c.resultado), false], ["Saldo final", f(c.closing), true],
    ] as [string, string, boolean][];
  };
  function summaryCard(s: MovementsSummary) {
    const merge = !single && s.effective.transfersIn.cents === s.effective.transfersOut.cents && s.projected.transfersIn.cents === s.projected.transfersOut.cents;
    const e = col(s.effective, s.currency, merge), p = col(s.projected, s.currency, merge);
    return (
      <div className="card mv-summary" key={s.currency} data-summary={s.currency}>
        <h3 title="Transferências não entram em receitas, despesas nem no resultado. O projetado inclui os planejados e vencidos com data prevista no período.">Resumo do período{view!.summary!.length > 1 ? ` — ${s.currency}` : ""}</h3>
        <table aria-label="Resumo do período">
          <thead><tr><th /><th>Efetivado</th><th>Projetado</th></tr></thead>
          <tbody>{e.map(([label, v, total], i) => <tr key={label} className={total ? "total" : ""}><td>{label}</td><td className="tabular">{v}</td><td className="tabular">{p[i]![1]}</td></tr>)}</tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {accounts.length === 0 ? (
        <div className="card stub"><h3>Nenhuma conta cadastrada</h3><p className="muted" style={{ margin: 0 }}>Cadastre uma conta em Contas Financeiras para ver as movimentações.</p></div>
      ) : (
        <>
          <div className="toolbar filters clean">
            <Select value={accountId} onChange={(e) => setAccountId(e.target.value)} name="statementAccount" aria-label="Conta"><option value={ALL}>Todas as contas</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency}){a.status === "inativa" ? " — inativa" : ""}</option>)}</Select>
            <div className="seg" role="group" aria-label="Situação">
              {QUICK.map(([k, l]) => <button key={k} aria-pressed={sit === k} onClick={() => setSit(k)} data-sit={k}>{l}</button>)}
            </div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar descrição" name="mvQ" aria-label="Buscar" style={{ width: 220 }} />
            <Btn variant="ghost" onClick={() => setAdv((v) => !v)} aria-expanded={adv} data-filters-toggle>Filtros{activeAdv ? ` (${activeAdv})` : ""}</Btn>
            <span className="spacer" />
          </div>
          {adv && (
            <div className="toolbar filters adv card">
              <Field label="De"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} name="from" /></Field>
              <Field label="Até"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} name="to" /></Field>
              <Field label="Situação"><Select value={sit} onChange={(e) => setSit(e.target.value)} name="mvSituation">{SITUATIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
              <Field label="Conciliação"><Select value={recon} onChange={(e) => setRecon(e.target.value)} name="mvRecon"><option value="">Todas</option><option value="true">Conciliadas</option><option value="false">Não conciliadas</option></Select></Field>
              <Field label="Tipo"><div className="type-chips" role="group" aria-label="Tipo">{TYPES.map(([k, l]) => <label key={k}><input type="checkbox" checked={types.includes(k)} onChange={() => toggleType(k)} name={`mvType-${k}`} />{l}</label>)}</div></Field>
              <Field label="Previsto até"><Input type="date" value={cutoff} min={view?.asOf} onChange={(e) => setCutoff(e.target.value)} name="mvCutoff" /></Field>
            </div>
          )}
          {view && (
            <div className="balance-line" aria-label="Saldos">
              {view.balances.totals.map((t) => (
                <div key={t.currency} style={{ display: "contents" }}>
                  <span><span className="muted">Saldo atual{!single ? ` (${t.currency}, ${t.accounts} ${t.accounts === 1 ? "conta" : "contas"})` : ""}</span> <strong className="tabular" data-balance-current>{formatMoney(t.current.decimal, t.currency)}</strong></span>
                  <span><span className="muted" data-projection-label>{projLabel}</span> <strong className="tabular" data-balance-projected>{formatMoney(t.projected.decimal, t.currency)}</strong></span>
                </div>
              ))}
            </div>
          )}
          {view && !single && view.balances.accounts.length > 0 && (
            <div className="card table-card acc-compact" style={{ marginBottom: 12 }}>
              <table aria-label="Saldos por conta" data-account-balances>
                <thead><tr><th>Conta</th><th className="num">Saldo atual</th><th className="num" title={projLabel}>Previsto até o fim do mês</th></tr></thead>
                <tbody>{view.balances.accounts.map((a) => <tr key={a.accountId} className={a.status === "inativa" ? "inactive" : ""}><td>{a.name}{a.status === "inativa" ? " — inativa" : ""}</td><td className="num tabular">{formatMoney(a.current.decimal, a.currency)}</td><td className="num tabular">{formatMoney(a.projected.decimal, a.currency)}</td></tr>)}</tbody>
              </table>
            </div>
          )}
          {state === "error" && <Notice kind="error">Não foi possível carregar as movimentações. Tente novamente.</Notice>}
          <div className="mv-layout">
            <div>
              {view?.summary?.map(summaryCard)}
              {detail && single && (
                <>
                  <div className="card mv-summary" data-account-detail>
                    <h3>Detalhamento</h3>
                    <table aria-label="Detalhamento das despesas do período"><tbody>
                      <tr><td>Despesas</td><td className="tabular">{formatMoney(detail.expenses.singles.decimal, detail.currency)}</td></tr>
                      <tr><td>Despesas fixas</td><td className="tabular">{formatMoney(detail.expenses.fixed.decimal, detail.currency)}</td></tr>
                      <tr><td>Compras parceladas</td><td className="tabular">{formatMoney(detail.expenses.installments.decimal, detail.currency)}</td></tr>
                    </tbody></table>
                  </div>
                  {detail.type === "conta_corrente" && (
                    <div className="card mv-summary" data-account-limit>
                      <h3>Limite</h3>
                      <table aria-label="Limite da conta"><tbody>
                        <tr><td>Limite da conta</td><td className="tabular">{formatMoney(detail.limit.total.decimal, detail.currency)}</td></tr>
                        <tr><td>Utilizado</td><td className="tabular">{formatMoney(detail.limit.used.decimal, detail.currency)}</td></tr>
                        <tr className="total"><td>Disponível</td><td className="tabular">{formatMoney(detail.limit.available.decimal, detail.currency)}</td></tr>
                      </tbody></table>
                    </div>
                  )}
                  {fullMonth && accCur && <PositionClosing accountId={accountId} month={from.slice(0, 7)} currency={accCur} canWrite={canWrite} version={flow.version} onStatus={setClosing} onChanged={onChanged} />}
                </>
              )}
            </div>
            <div>
              {view && view.lines.length === 0 && state === "ok" && <div className="card stub"><h3>Nenhuma movimentação com estes filtros</h3><p className="muted" style={{ margin: 0 }}>Ajuste o período, a situação ou o tipo. Lançamentos cancelados só aparecem com a situação “Cancelados” ou “Todos”.</p></div>}
              {view && view.lines.length > 0 && (
                <div className="card table-card">
                  <SituationLegend />
                  <table aria-label="Movimentações" data-statement data-movements>
                    <thead><tr><th className="sit-col" aria-label="Situação" /><th>Data</th><th>Movimentação</th>{!single && <th>Conta</th>}<th className="num">Valor</th>{single && <th className="num">Saldo efetivado</th>}{single && <th className="num">Saldo projetado</th>}<th>Conciliação</th><th aria-label="Ações" /></tr></thead>
                    <tbody>{body(view)}</tbody>
                  </table>
                  <ScrollSentinel watch={view.lines.length} onReach={() => setPages((p) => (view.lines.length < view.total && LIMIT * p < MAX_LINES && state !== "loading" ? p + 1 : p))} disabled={view.lines.length >= view.total} />
                  <div className="pager small muted">
                    {view.lines.length < view.total ? `${view.lines.length} de ${view.total} movimentações` : `${view.total} ${view.total === 1 ? "movimentação" : "movimentações"}`}
                    {view.lines.length < view.total && view.lines.length >= MAX_LINES && <span> · refine o período ou os filtros para ver o restante</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {dlg?.k === "match" && <ManualDialog realm={realm} m={dlg.m} onClose={() => setDlg(null)} onDone={(text) => { setDlg(null); setMsg({ kind: "ok", text }); void load(); flow.bump(); }} />}
      {dlg?.k === "undo" && <UndoDialog realm={realm} reconciliationId={dlg.m.reconciliationId!} title={dlg.m.title} onClose={() => setDlg(null)} onDone={(text) => { setDlg(null); setMsg({ kind: "ok", text }); void load(); flow.bump(); }} onError={(text) => { setDlg(null); setMsg({ kind: "error", text }); void load(); }} />}
      {dlg?.k === "history" && <HistoryModal title={`Histórico da conciliação — ${dlg.m.title}`} path={`/api/${realm}/reconciliations/${dlg.m.reconciliationId}/history`} onClose={() => setDlg(null)} />}
    </>
  );
}

function endOfMonthIso(from: string) { const [y, m] = from.split("-").map(Number) as [number, number]; const d = new Date(Date.UTC(y, m, 0)).getUTCDate(); return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`; }
